import React, { useCallback, useEffect, useRef, useState } from "react";
import { GetResidualFileBase64, ResidualFileGet } from "../../../../../api";
import ShowResidualPdfModal from "../../../../../components/modals/show-residual-pdf/show-residual-pdf";
import ExcelFile from "../../../../assets/svg/icons/common/excel-file";
import Download from "../../../../assets/svg/icons/common/Download";
import UploadIcon from "../../../../assets/svg/icons/common/upload";
import Select from "../../../../components/form/select";
import Spinner from "../../../../components/Loaders/Spinner";
import Accordion from "../../../../components/Accordion";
import useCommonStore from "../../../../stores/common";
import useDomainStore from "../../../../stores/domain";
import useOrganizationStore from "../../../../stores/organization";
import AddResidualsModal from "./add-residuals-modal";
import styles from "./styles.module.scss";
import toast from "react-hot-toast";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FileItem = ({ month, year, uploaded, hasPdf, onFileClick, onDownloadClick }) => (  
  <div className={styles.file} >
    <ExcelFile />
    <div className={styles.fileInfo} onClick={() => onFileClick(month, year)} >
      <span className={styles.fileName} title={`${month}_${year}.xlsx`}  >{`${month}_${year}.xlsx`}</span>
      {hasPdf && <span className={styles.fileSize}>PDF available</span>}
    </div>
    <div onClick={() => onDownloadClick(month, year)}>
      <Download />
    </div>      
  </div>     
);

const Residuals = () => {
  const { isKurvDomain } = useDomainStore();
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);
  const isMobile = useMediaQuery(768);
  const [expandedItems, setExpandedItems] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [docModalFile, setDocModalFile] = useState(null);

  const expandDetails = useCommonStore((state) => state.expandDetails);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [residualArray, setResidualArray] = useState([]);
  const [residualPdfData, setResidualPdfData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [allYearsData, setAllYearsData] = useState({});

  const handleItemToggle = (id) => {
    const isExpanding = !expandedItems.includes(id);
    
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );

    if (isExpanding && isMobile) {
      const yearFromId = id.replace('_year', '');
      const yearNumber = parseInt(yearFromId, 10);
      if (!isNaN(yearNumber)) {
        fetchResiduals(yearNumber, true);
      }
    }
  };

  const populateLocalArray = (year) => {
    let ResidualFinalArray = [];
    if (ResidualFinalArray[year] === undefined) {
      ResidualFinalArray[year] = [];
    }
    for (let i = 1; i <= 12; i++) {
      ResidualFinalArray[year][i] = 0;
    }
    return ResidualFinalArray;
  };

  const fetchResiduals = useCallback(
    (year, isMobileView = false) => {
      if (isMobileView && allYearsData.residuals?.[year]) {
        return;
      }

      let ResidualFinalArray = [];
      let ResidualFinalPDFArray = [];
      
      if (!isMobileView) {
        setLoading(true);
      }

      ResidualFileGet({
        organization_id: selectedOrganizationId,
        year: year,
      })
        .then((response) => {
          if (response.success) {
            // ToDo: I have directly copied this as is from v1. Simplify this down the line.
            // There's an `empty` item which is being added to the array, not sure why.
            const residual_data = response.data;
            if (residual_data.length == 0) {
              ResidualFinalArray = populateLocalArray(year);
              ResidualFinalPDFArray = populateLocalArray(year);
            } else {
              residual_data.map((item) => {
                if (ResidualFinalArray[item.year] === undefined) {
                  ResidualFinalArray[item.year] = [];
                }
                if (ResidualFinalPDFArray[item.year] === undefined) {
                  ResidualFinalPDFArray[item.year] = [];
                }
                ResidualFinalArray[item.year][item.month] = item.uploaded;
                ResidualFinalPDFArray[item.year][item.month] =
                  item.converted_file ? 1 : 0;
              });
            }

            if (isMobileView) {
              setAllYearsData((prev) => ({
                residuals: { ...prev.residuals, ...ResidualFinalArray },
                pdfs: { ...prev.pdfs, ...ResidualFinalPDFArray },
              }));
            } else {
              setResidualArray(ResidualFinalArray);
              setResidualPdfData(ResidualFinalPDFArray);
            }
            
            if (!isMobileView) {
              setLoading(false);
            }
          }
        })
        .catch((error) => {
          console.error(error);
          if (!isMobileView) {
            setLoading(false);
          }
        });
    },
    [selectedOrganizationId, allYearsData],
  );

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    fetchResiduals(currentYear, isMobile);
  }, [fetchResiduals, isMobile]);

  const validateAndSetFile = (file) => {
    if (!file) return false;

    const fileType = file.name.split(".").pop().toLowerCase();
    if (fileType !== "xls" && fileType !== "xlsx") {
      toast.error("Please select a valid XLS/XLSX file", { duration: 5000 });
      return false;
    }

    setFile(file);
    setUploadModalOpen(true);
    return true;
  };

  const handleFileDrop = (e) => {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  useEffect(() => {
    if (dropZoneRef.current) {
      dropZoneRef.current.addEventListener("drop", handleFileDrop);
      return () => {
        dropZoneRef.current.removeEventListener("drop", handleFileDrop);
      };
    }
  }, []);

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
    event.target.value = "";
  };

  const handleChangeYear = (value) => {
    setYear(value?.value);
    fetchResiduals(value?.value);
  };

  const handleFileClick = useCallback(
    (month, year) => {
      setLoadingFile(true);
      GetResidualFileBase64({
        organization_id: selectedOrganizationId,
        month,
        year,
      })
        .then((response) => {
          if (response.success) {
            setDocModalFile(response);
            setOpenDocModal(true);
          } else {
            setOpenDocModal(false);
            setDocModalFile([]);
          }
          setLoadingFile(false);
        })
        .catch((error) => {
          console.error("Error loading file:", error);
          setLoadingFile(false);
        });
    },
    [selectedOrganizationId],
  );

  const handleDownloadClick = (month, year) => {
    const final_url = `/residuals-download/${selectedOrganizationId}/${year}/${month}?time=${Math.floor(Date.now() / 1000)}`;
    window.open(final_url, "_blank"); 
  };

  return (
    <div
      className={`${styles.content} ${expandDetails ? styles.expanded : ""} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      <div className={styles.fileList}>
        {!isMobile && (
          <div className={styles.filterContainer}>
            <Select
              options={(() => {
                const currentYear = new Date().getFullYear();
                const years = [];
                for (let i = currentYear; i >= currentYear - 15; i--) {
                  years.push({ value: i.toString(), label: i.toString() });
                }
                return years;
              })()}
              value={{
                value: year,
                label: year,
              }}
              onChange={handleChangeYear}
              placeholder="Select year"
            />
          </div>
        )}

        {loading && (
          <div className={styles.loading}>
            <Spinner />
          </div>
        )}

        {!isMobile && !loading && residualArray[year] && (
          <div className={styles.monthFiles}>
            {MONTHS.map((month, index) => {
              const isUploaded = residualArray[year][index + 1] === 1;
              const hasPdf = residualPdfData[year]?.[index + 1] === 1;

              return isUploaded ? (
                <FileItem
                  key={`${month}-${year}`}
                  month={month}
                  year={year}
                  uploaded={isUploaded}
                  hasPdf={hasPdf}
                  onFileClick={() => handleFileClick(month, year)}
                  onDownloadClick={() => handleDownloadClick(month, year)}
                />
              ) : null;
            })}
          </div>
        )}

        {isMobile && !loading && (
          <div className={styles.mobileAccordion}>
            {(() => {
              const currentYear = new Date().getFullYear();
              const years = [];
              for (let i = currentYear; i >= currentYear - 15; i--) {
                years.push(i);
              }
              return years.map((yearItem) => {
                const isExpanded = expandedItems.includes(`${yearItem}_year`);
                const yearDataLoaded = allYearsData.residuals?.[yearItem];
                const hasFiles = yearDataLoaded && 
                  Object.values(allYearsData.residuals[yearItem]).some((v) => v === 1);

                return (
                  <div key={yearItem} className={styles.yearAccordionItem}>
                    <Accordion
                      items={[
                        {
                          id: `${yearItem}_year`,
                          title: `${yearItem} year`,
                          content: !yearDataLoaded ? (
                            <div className={styles.yearLoading}>
                              <Spinner />
                            </div>
                          ) : hasFiles ? (
                            <div className={styles.yearContent}>
                              {MONTHS.map((month, index) => {
                                const isUploaded = allYearsData.residuals[yearItem]?.[index + 1] === 1;
                                const hasPdf = allYearsData.pdfs[yearItem]?.[index + 1] === 1;

                                return isUploaded ? (
                                  <FileItem
                                    key={`${month}-${yearItem}`}
                                    month={month}
                                    year={yearItem}
                                    uploaded={isUploaded}
                                    hasPdf={hasPdf}
                                    onFileClick={() => handleFileClick(month, yearItem)}
                                    onDownloadClick={() => handleDownloadClick(month, yearItem)}
                                  />
                                ) : null;
                              })}
                            </div>
                          ) : (
                            <div className={styles.yearNoData}>
                              <p>No data to display</p>
                            </div>
                          ),
                        },
                      ]}
                      expandedItems={expandedItems}
                      onItemToggle={handleItemToggle}
                    />
                  </div>
                );
              });
            })()}
          </div>
        )}

        {!loading &&
          !isMobile &&
          (!residualArray[year] ||
            Object.values(residualArray[year] || {}).every((v) => v === 0)) && (
            <div className={styles.noData}>
              <p>No data to display</p>
            </div>
          )}
      </div>

      <div className={styles.uploadSection}>
        <h2>Upload Residuals</h2>
        <div
          className={styles.dropZone}
          onClick={() => fileInputRef.current.click()}
          ref={dropZoneRef}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".xls,.xlsx"
            style={{ display: "none" }}
          />
          <div className={styles.uploadIcon}>
            <UploadIcon />
          </div>
          <div className={styles.uploadText}>
            <p>
              <span className={styles.uploadTextDesktop}>
                Upload documents by drag and drop or{" "}
                <span className={styles.uploadButton}>click to upload</span>
              </span>
              <span className={styles.uploadTextMobile}>Upload documents</span>
            </p>
            <p className={styles.fileTypes}>xlsx, xls</p>
          </div>
        </div>
      </div>

      <AddResidualsModal
        onClose={() => {
          setUploadModalOpen(false);
          fetchResiduals(year);
        }}
        open={uploadModalOpen}
        file={file}
        clearFile={() => {
          setFile(null);
          fileInputRef.current.value = "";
        }}
      />

      {openDocModal && (
        <ShowResidualPdfModal
          isOpen={openDocModal}
          close={() => setOpenDocModal(false)}
          file={docModalFile}
          fileType={"pdf"}
        />
      )}
    </div>
  );
};

export default Residuals;
